import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { supabase } from "../../../config/supabaseClient";
import { computeWindow } from "../../../Utils/customHooks/UpToDateContent/useLoadYesterdayLinks";
import { getAllDatesFromSelection } from "../../../Utils/StayUpToDate/RenderComponent";
import { StaticVariables } from "../../../Utils/StaticVariables";
import { buildPhraseIlikePatterns } from "../../../components/SearchInput/phraseSearchHelpers";
import {
  deriveAllowedPlatforms,
  readPlatformFilterFromDetails,
  shouldUseShopKeywordRpc,
  type ShowFilter,
} from "../LandingPageCarousels/QueryBasedCarousel/filterUtils";
import {
  CONTENT_FEED_ALL_FEED_TYPES,
  CONTENT_FEED_ALL_SOCIALS,
} from "../StayUpToDate/AddComponentPicker/contentFeedSelection";
import { getContentFeedDateWindowFromComponent } from "../StayUpToDate/AddComponentPicker/contentFeedTimeframe";

dayjs.extend(utc);
dayjs.extend(timezone);

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Static checks only (config missing post_ids / query, etc.).
 */
export function predictStayUpToDateTabHasData(
  row: Record<string, unknown> | undefined | null,
): boolean {
  if (!row) return false;

  const type = String(row.type ?? "").toLowerCase().trim();
  const query = String((row as { query?: string }).query ?? "").trim();

  const raw = (row as { post_ids?: unknown }).post_ids;
  const ids =
    Array.isArray(raw)
      ? raw.map(String).join(",")
      : typeof raw === "string"
        ? raw
        : "";
  const hasPostIds = ids.split(",").some((s) => s.trim().length > 0);

  if (type === "query-links" || type === "query-content" || type === "content-series") {
    if (query.length > 0) return true;
    if (type === "content-series") {
      const customOrder = String(
        (row as { custom_order?: string }).custom_order ?? "",
      ).trim();
      return customOrder.length > 0;
    }
    return false;
  }
  if (type === "links" || type === "content" || type === "shoppable") {
    return hasPostIds;
  }
  if (type === "reels" || type === "featured" || type === "videos") {
    return true;
  }
  // Recent Content platform "All" — live feed via get_content_all_platforms_*.
  if (type === "all") {
    return true;
  }

  const dynamicOk = new Set([
    "trending products",
    "trending content",
    "product-bank",
    "store-product",
    "products-carousel",
    "links-section",
    "carousel-collection",
    "banner",
    "highlight",
    "embed-form",
    "newsletter-form",
    "campaign",
    "daywise",
  ]);
  if (dynamicOk.has(type)) return true;

  const dn = String((row as { display_name?: string }).display_name ?? "").trim();
  if (
    ["Yesterday", "This Week", "This Month", "This Week Content"].includes(dn)
  ) {
    return true;
  }

  return true;
}

function buildSelectedDatesArrayForProbe(
  displayNameRaw: string | undefined,
): string[] | null {
  if (!displayNameRaw?.trim()) return null;
  const selectedDays = displayNameRaw.split(",").map((s) => s.trim());
  if (selectedDays.length === 0) return null;
  const dates = getAllDatesFromSelection(selectedDays);
  if (dates.length === 0) return null;
  return dates.map((dateStr) => {
    const date = new Date(dateStr);
    return dayjs(date).tz(userTimezone).format("YYYY-MM-DD");
  });
}

/**
 * Same date window + RPC as {@link useLoadYesterdayLinks} (get_shoplink_content).
 */
async function probeShoplinkDayWindow(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
): Promise<boolean> {
  const displayName = String(row.display_name ?? "").trim();
  const mappings = StaticVariables().dayMappings as Record<string, unknown>;

  let numberOfDays: number[] | null = null;
  let componentType = "";
  if (displayName === "Yesterday") numberOfDays = [1];
  else if (displayName === "This Week") numberOfDays = [7];
  else if (displayName === "This Month") numberOfDays = [30];
  else if (mappings[displayName] || String(row.type ?? "").toLowerCase() === "daywise") {
    numberOfDays = [];
    componentType = "daywise";
  } else {
    return false;
  }

  const selectedDaySegments = row.display_name
    ? String(row.display_name).split(",").map((s) => s.trim())
    : undefined;

  const windowRange = computeWindow(
    numberOfDays,
    componentType,
    selectedDaySegments,
  );
  if (!windowRange.start || !windowRange.end) return false;

  const startDate = windowRange.start.split(" ")[0];
  const endDate = windowRange.end.split(" ")[0];

  const filterRaw = (row as { shoppable_content_filter?: string })
    .shoppable_content_filter;
  const activeFilter = (filterRaw || "both").toLowerCase();
  const sv = StaticVariables();
  let socials = sv.PLATFORMS as string[];
  if (activeFilter === "content") socials = sv.CONTENT_PLATFORMS;
  else if (activeFilter === "products") socials = sv.PRODUCT_PLATFORMS;

  const customOrderRaw = (row as { custom_order?: string }).custom_order;
  const customOrder =
    typeof customOrderRaw === "string" && customOrderRaw.trim()
      ? customOrderRaw.split(",").map((s: string) => s.trim()).filter(Boolean)
      : [];

  const removedPostIds = (row as { removed_postids?: unknown }).removed_postids;
  const processedRemovedPostIds = Array.isArray(removedPostIds)
    ? removedPostIds.map(String).filter(Boolean)
    : typeof removedPostIds === "string"
      ? removedPostIds.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

  const order = (row as { content_order?: string | boolean }).content_order;
  const normalizedOrder =
    order === false || order === "false" ? "ASC" : order;

  const selectedDatesArray = buildSelectedDatesArrayForProbe(
    String(row.display_name ?? ""),
  );

  const { data: searchResult, error } = await supabase.rpc(
    "get_shoplink_content",
    {
      userid: userId,
      user_name: lcUser,
      start_date: startDate,
      end_date: endDate,
      post_ids: [],
      order_type: String(normalizedOrder ?? "DESC"),
      custom_order: customOrder,
      socials,
      limit_num: 1,
      offset_num: 0,
      removed_post_ids: processedRemovedPostIds,
      component_type: componentType || null,
      selected_dates: selectedDatesArray,
      user_timezone: userTimezone,
    },
  );

  if (error) return false;
  const responseData = searchResult?.data || [];
  const totalCount =
    typeof searchResult?.total === "number" ? searchResult.total : 0;
  return (
    totalCount > 0 ||
    (Array.isArray(responseData) && responseData.length > 0)
  );
}

/**
 * Same RPC shape as {@link useLoadYesterdayContent} for day-window CONTENT feeds.
 */
async function probeDayWindowContent(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
  days: number,
): Promise<boolean> {
  const sv = StaticVariables();
  const contentPlatforms = sv.CONTENT_PLATFORMS;
  const contentOrder = String(
    (row as { content_order?: string }).content_order || "DESC",
  );
  const customOrderPostIds =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  const removedPostIds = (row as { removed_postids?: unknown }).removed_postids;
  const processedRemovedPostIds = Array.isArray(removedPostIds)
    ? removedPostIds.map(String).filter(Boolean)
    : typeof removedPostIds === "string"
      ? removedPostIds.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

  let numDayStart = "";
  let numDayEnd = "";
  if (days === 30) {
    numDayStart = dayjs()
      .utc()
      .subtract(1, "month")
      .format("YYYY-MM-DD HH:mm:ss");
    numDayEnd = dayjs().utc().format("YYYY-MM-DD HH:mm:ss");
  } else {
    numDayStart = dayjs()
      .utc()
      .subtract(days, "day")
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    numDayEnd =
      days === 7
        ? dayjs().utc().format("YYYY-MM-DD HH:mm:ss")
        : dayjs()
            .utc()
            .subtract(days, "day")
            .endOf("day")
            .format("YYYY-MM-DD HH:mm:ss");
  }

  const { data: result, error } = await supabase.rpc("get_yesterday_content", {
    userid: userId,
    user_name: lcUser,
    start_date: numDayStart,
    end_date: numDayEnd,
    post_ids: [],
    order_type: contentOrder,
    content_platforms: contentPlatforms,
    custom_order: customOrderPostIds,
    limit_num: 1,
    offset_num: 0,
    removed_post_ids: processedRemovedPostIds,
  });

  if (error) return false;
  const responseData = result?.data ?? result ?? [];
  const total = typeof result?.total === "number" ? result.total : 0;
  const arr = Array.isArray(responseData) ? responseData : [];
  return total > 0 || arr.length > 0;
}

async function probeThisWeekContent(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
): Promise<boolean> {
  return probeDayWindowContent(row, userId, lcUser, 7);
}

async function probeCustomOrderedPostIds(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
  feedType: string,
): Promise<boolean> {
  const raw = (row as { post_ids?: unknown }).post_ids;
  const postIdsArr = Array.isArray(raw)
    ? raw.map(String).filter(Boolean)
    : typeof raw === "string"
      ? raw.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
  if (postIdsArr.length === 0) return false;

  const staticVars = StaticVariables();
  const filterRaw = (row as { shoppable_content_filter?: string })
    .shoppable_content_filter;
  const activeFilter = (
    filterRaw ||
    (feedType === "content" ? "content" : "products")
  ).toLowerCase();

  let platforms = staticVars.PLATFORMS as string[];
  if (activeFilter === "content") platforms = staticVars.CONTENT_PLATFORMS;
  else if (activeFilter === "products") platforms = staticVars.PRODUCT_PLATFORMS;

  const customOrderRaw = (row as { custom_order?: string }).custom_order;
  const customOrder =
    typeof customOrderRaw === "string" && customOrderRaw.trim()
      ? customOrderRaw.split(",").map((s: string) => s.trim()).filter(Boolean)
      : null;

  const { data: rpcResult, error } = await supabase.rpc(
    "get_custom_content_orderedd",
    {
      userid: userId,
      user_name: lcUser,
      post_ids: postIdsArr,
      order_type: String(
        (row as { content_order?: string }).content_order || "DESC",
      ),
      custom_order: customOrder,
      platforms,
      limit_num: 1,
      removed_post_ids: [],
    },
  );

  if (error) return false;
  const responseData = rpcResult?.data ?? rpcResult ?? [];
  const total = typeof rpcResult?.total === "number" ? rpcResult.total : 0;
  const arr = Array.isArray(responseData) ? responseData : [];
  return total > 0 || arr.length > 0;
}

async function probeTypeContentFeed(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
  platform: string,
  feedType: string,
): Promise<boolean> {
  const orderType = String(
    (row as { content_order?: string }).content_order || "DESC",
  );
  const customOrder = typeof (row as { custom_order?: string }).custom_order === "string"
    ? (row as { custom_order: string }).custom_order
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const pinned = typeof (row as { pinned_posts?: string }).pinned_posts === "string"
    ? (row as { pinned_posts: string }).pinned_posts
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const statusFilter = String(
    (row as { shoppable_status_filter?: string }).shoppable_status_filter ||
      "all",
  );

  if (feedType.toUpperCase() === "STORY") {
    const today = dayjs().utc().format("YYYY-MM-DD");
    const yesterday = dayjs().utc().subtract(7, "day").format("YYYY-MM-DD");
    const { data: content, error } = await supabase.rpc(
      "get_storytypecontenttteonord_pin_new",
      {
        userid: userId,
        user_name: lcUser,
        platform_name: platform,
        feed_type: "STORY",
        start_date: yesterday,
        end_date: today,
        order_type: orderType,
        custom_order: customOrder,
        limit_num: 1,
        offset_num: 0,
        pinned_post_ids: pinned,
        shoppable_status_filter: statusFilter,
      },
    );
    if (error) return false;
    if (Array.isArray(content)) return content.length > 0;
    const arr = Array.isArray((content as any)?.data) ? (content as any).data : [];
    const total = Number((content as any)?.total ?? 0);
    return total > 0 || arr.length > 0;
  }

  const feedTypes =
    platform.toLowerCase() === "linkedin" ? ["POST"] : [feedType.toUpperCase()];
  const { data: content, error } = await supabase.rpc(
    "get_content_type_postsspinn_new",
    {
      userid: userId,
      user_name: lcUser,
      platform_name: platform,
      feed_type: feedTypes,
      order_type: orderType,
      custom_order: customOrder,
      limit_num: 1,
      offset_num: 0,
      pinned_post_ids: pinned,
      shoppable_status_filter: statusFilter,
    },
  );

  if (error) return false;
  const arr = Array.isArray((content as any)?.data) ? (content as any).data : [];
  const total = Number((content as any)?.total ?? 0);
  return total > 0 || arr.length > 0;
}

async function probeAllPlatformsContentFeed(
  row: Record<string, unknown>,
  userId: string,
  lcUser: string,
): Promise<boolean> {
  const orderType = String(
    (row as { content_order?: string }).content_order || "DESC",
  );
  const customOrder =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const pinned =
    typeof (row as { pinned_posts?: string }).pinned_posts === "string"
      ? (row as { pinned_posts: string }).pinned_posts
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const statusFilter = String(
    (row as { shoppable_status_filter?: string }).shoppable_status_filter ||
      "all",
  );
  const dateWindow = getContentFeedDateWindowFromComponent(row);
  const allBase = {
    userid: userId,
    filter: [...CONTENT_FEED_ALL_FEED_TYPES],
    query_str: "",
    user_name: lcUser,
    socials: [...CONTENT_FEED_ALL_SOCIALS],
    order_type: orderType,
    custom_order: customOrder,
    limit_num: 1,
    offset_num: 0,
    pinned_post_ids: pinned,
    shoppable_status_filter: statusFilter,
  };
  const { data: content, error } = dateWindow.applyFilter
    ? await supabase.rpc("get_content_all_platforms_dated", {
        ...allBase,
        start_date: dateWindow.startDate,
        end_date: dateWindow.endDate,
        component_type: dateWindow.componentType,
        selected_dates: dateWindow.selectedDates,
        user_timezone: userTimezone,
      })
    : await supabase.rpc("get_content_all_platforms_new", allBase);
  if (error) return false;
  if (Array.isArray(content)) return content.length > 0;
  const arr = Array.isArray((content as { data?: unknown })?.data)
    ? ((content as { data: unknown[] }).data as unknown[])
    : [];
  const total = Number((content as { total?: number })?.total ?? 0);
  return total > 0 || arr.length > 0;
}

/**
 * Lightweight RPC probes for tab types where empty feeds are common.
 * Other types return true (same as in-render loaders).
 */
export async function probeTemplateTabHasFeedItems(
  userId: string,
  username: string,
  row: Record<string, unknown> | undefined | null,
): Promise<boolean> {
  if (!row || !predictStayUpToDateTabHasData(row)) return false;

  const type = String(row.type ?? "").toLowerCase().trim();
  const lcUser = username.trim().toLowerCase();
  const displayName = String(
    (row as { display_name?: string }).display_name ?? "",
  ).trim();

  if (displayName === "This Week Content") {
    return probeThisWeekContent(row, userId, lcUser);
  }

  // Website Recent Content day-windows are content feeds. Prefer content probe
  // unless this row is an explicit shoppable/products LiB fork.
  {
    const nameLc = String((row as { name?: string }).name ?? "")
      .trim()
      .toLowerCase();
    const filterLc = String(
      (row as { shoppable_content_filter?: string }).shoppable_content_filter ??
        "",
    )
      .trim()
      .toLowerCase();
    const isLibShopFork =
      nameLc.includes("shoppable") || filterLc === "products";
    if (!isLibShopFork) {
      if (displayName === "Yesterday") {
        return probeDayWindowContent(row, userId, lcUser, 1);
      }
      if (displayName === "This Month") {
        return probeDayWindowContent(row, userId, lcUser, 30);
      }
      if (displayName === "This Week") {
        return probeDayWindowContent(row, userId, lcUser, 7);
      }
    }
  }

  // Content-tab “type feeds” (twitter/youtube/tiktok/etc.) — probe the same RPC the loaders use
  {
    const typeLc = type.toLowerCase();
    const dnLc = displayName.toLowerCase();
    const match = (v: string) => v === typeLc || v === dnLc;

    if (match("tiktok")) {
      return probeTypeContentFeed(row, userId, lcUser, "tiktok", "TIKTOK");
    }
    if (match("twitter")) {
      return probeTypeContentFeed(row, userId, lcUser, "twitter", "TWEET");
    }
    if (match("youtube")) {
      return probeTypeContentFeed(row, userId, lcUser, "youtube", "VIDEO");
    }
    if (match("podcast")) {
      return probeTypeContentFeed(row, userId, lcUser, "podcast", "PODCAST");
    }
    if (match("linkedin")) {
      return probeTypeContentFeed(row, userId, lcUser, "linkedin", "LINKEDIN");
    }
    if (match("instagram story") || match("story")) {
      return probeTypeContentFeed(row, userId, lcUser, "instagram", "STORY");
    }
    if (match("all")) {
      return probeAllPlatformsContentFeed(row, userId, lcUser);
    }
  }

  const mappings = StaticVariables().dayMappings as Record<string, unknown>;
  const isDayBasedShoplink =
    displayName === "Yesterday" ||
    displayName === "This Week" ||
    displayName === "This Month" ||
    Boolean(mappings[displayName]) ||
    type === "daywise";

  if (isDayBasedShoplink) {
    return probeShoplinkDayWindow(row, userId, lcUser);
  }

  if (type === "links" || type === "content") {
    return probeCustomOrderedPostIds(row, userId, lcUser, type);
  }

  if (type === "query-links" || type === "query-content" || type === "content-series") {
  const queryTerm = String((row as { query?: string }).query ?? "").trim();
  if (!queryTerm) return false;

  const isShopLinks = shouldUseShopKeywordRpc(
    type,
    (row as { shoppable_content_filter?: string }).shoppable_content_filter,
  );
  const isSeriesContent = type === "content-series";
  const platformFilter = readPlatformFilterFromDetails(
    (row as { details?: unknown }).details,
  );
  const showFilter = (
    ((row as { shoppable_content_filter?: string }).shoppable_content_filter ||
      "both") as string
  ).toLowerCase() as ShowFilter;
  const showSocials = deriveAllowedPlatforms(
    platformFilter,
    showFilter,
    isShopLinks,
  );

  const sv = StaticVariables();
  const normalizedQuery = queryTerm.toLowerCase().replace(/\s+/g, " ").trim();
  const contentOrderStr = String(
    (row as { content_order?: string }).content_order ||
      sv.DEFAULT_CONTENT_ORDER ||
      "DESC",
  );

  const customOrderPostIds =
    typeof (row as { custom_order?: string }).custom_order === "string"
      ? (row as { custom_order: string }).custom_order
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  let words = normalizedQuery.split(" ");
  let localContentOrder = contentOrderStr;
  if (
    words.length === 1 &&
    !sv.SEARCH_EXCEPTIONS.includes(normalizedQuery)
  ) {
    const suffixRegex = /(s)$/i;
    if (suffixRegex.test(words[0])) {
      words = [words[0].replace(suffixRegex, "")];
    }
  }
  if (words.includes("shop") || words.includes("buy")) {
    localContentOrder = "shoppable";
    words = words.filter((w) => w !== "shop" && w !== "buy");
  }
  const filteredWords = words.filter(
    (word: string) => !sv.connectingWords.includes(word.toLowerCase()),
  );
  const joined = filteredWords.join(" ");
  const pgQuery = joined.toLowerCase().replace(/\s+/g, " & ");
  const singlePatterns = buildPhraseIlikePatterns(normalizedQuery);
  const localOrder = localContentOrder.split(" ")[0].toLowerCase();

  const removedPostIds = (row as { removed_postids?: unknown }).removed_postids;
  const normalizedRemovedPostIds = Array.isArray(removedPostIds)
    ? removedPostIds.map(String).filter(Boolean)
    : typeof removedPostIds === "string"
      ? removedPostIds.split(",").map((id) => id.trim()).filter(Boolean)
      : [];

  const rpcFunction = isShopLinks ? "query_based_shop" : "query_based_cont";

  const { data: searchResult, error } = await supabase.rpc(rpcFunction, {
    query_string: pgQuery,
    userid: userId,
    username: lcUser,
    socials: showSocials,
    content_order: localOrder,
    single: singlePatterns,
    post_order: String(
      (row as { content_order?: string }).content_order || "DESC",
    ),
    custom_order: customOrderPostIds,
    limit_num: 1,
    offset_num: 0,
    removed_post_ids: normalizedRemovedPostIds,
  });

  if (error) return false;
  const responseData = searchResult?.data || [];
  const totalCount = searchResult?.total || 0;
  return (
    totalCount > 0 ||
    (Array.isArray(responseData) && responseData.length > 0)
  );
  }

  if (type === "reels" || type === "featured" || type === "videos") {
    const rawPid = (row as { post_ids?: unknown }).post_ids;
    const postIdsArr = Array.isArray(rawPid)
      ? rawPid.map(String).filter(Boolean)
      : typeof rawPid === "string"
        ? rawPid.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
    if (postIdsArr.length > 0) {
      return probeCustomOrderedPostIds(row, userId, lcUser, type);
    }
    if (type === "reels") {
      return probeTypeContentFeed(row, userId, lcUser, "instagram", "REELS");
    }
    if (type === "featured") {
      // Same loader as FeaturedData (platform=featured, feedType=FEATURE).
      return probeTypeContentFeed(row, userId, lcUser, "featured", "FEATURE");
    }
    return false;
  }

  // Trending feeds are dynamic; keep tabs eligible here. Empty ones are still
  // removed by onTabFeedResolved. Add pills for these stay always enabled.
  if (type === "trending products" || type === "trending content") {
    return true;
  }

  // Unknown / unprobed types: do not assume they have feed items.
  // Optimistic `true` locked Add-carousel pills for empty tabs the page later hides.
  return false;
}
